<template>
  <o-dialog
    ref="dialog"
    :title="title"
    :info="info"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.confirm'), type: 'confirm', variant: 'success', callback: handleConfirm },
    ]"
    :loading="loading"
    :portal="portal"
  >
    <template #heading>
      <img :src="`${api}/v1/integration/icon?guid=${integration.guid}&type=&size=256`" class="h-10 w-10 rounded-xl mx-auto mt-3">
    </template>
  </o-dialog>
</template>

<script setup lang="ts">
import type { Integration } from '~/_api/models/integration'

interface Props {
  portal?: string
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'success'): void
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<Emits>()
const { $api, $i18n, $notification, $config } = useNuxtApp()

const dialog = ref<any>()
const integration = ref<any>(null)
const action = ref<string | null>(null)

const actions = computed(() => {
  return integration.value?.actions
})

const title = computed(() => {
  if (integration.value) {
    return integration.value.name
  }

  return ''
})

const info = computed(() => {
  if (action.value && integration.value) {
    const act = actions.value[action.value]
    return act.title
  }

  return ''
})

const api = computed(() => {
  return ($config as any).public.api
})

const open = ({ integration: intg, action: act }: { integration: Integration; action: string }) => {
  integration.value = intg
  action.value = act

  dialog.value.open()
}

const handleConfirm = async () => {
  emit('update:loading', true)

  try {
    const act = action.value!
    const response = await $api.integration.runAction(
      integration.value.guid,
      { action: act }
    )

    if (response.success) {
      emit('success')

      $notification.toast({
        title: $i18n.t('general.success'),
        text: `${integration.value.name}: ${actions.value[act].title}`,
        variant: 'success',
      })

      return true
    }

    $notification.error({ errors: response.errors })
  } catch (e: any) {
    $notification.toast({
      title: $i18n.t('general.error'),
      text: $i18n.t('components.integration.actions.action.errorInfo'),
    })
  } finally {
    emit('update:loading', false)
  }

  return false
}

defineExpose({
  open,
})
</script>
